import {
  EVENT_STATUSES,
  EVENT_TYPES,
  type EventStatus,
  type EventType,
} from "@/api/events.service";
import type { ImageRef } from "@/api/events/files";

/**
 * Client-side mirror of content-api's `src/schemas/events.ts`. The server is still the
 * authority — this only saves Events Media a round trip and names the field that is wrong.
 */

export type EventFormValues = {
  title: string;
  type: EventType | "";
  startDate: string;
  startTime: string;
  timezone: string;
  locationShort: string;
  venueAddress: string;
  featuredImage: ImageRef | null;
  linkUrl: string;
  status: EventStatus;
};

export type EventFormErrors = Partial<Record<keyof EventFormValues, string>>;

export const EVENT_TYPE_OPTIONS = EVENT_TYPES.map(type => ({ value: type, label: type }));

export const EVENT_STATUS_OPTIONS: Array<{ value: EventStatus; label: string }> = [
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
  { value: "cancelled", label: "Cancelled" },
];

export const EVENT_STATUS_VALUES = EVENT_STATUSES;

/**
 * The short location is mandatory for every type except Webinar — a webinar has no city.
 * See the event-management spec, "Location is mandatory except for webinars".
 */
export function locationShortRequiredForType(type: EventType | ""): boolean {
  return type !== "" && type !== "Webinar";
}

/**
 * Legal 500's editorial time zone. "Today" is fixed to it so the past-date warning does not
 * depend on where the staff user's laptop happens to be — the same boundary content-api uses
 * to decide what is upcoming (design D4).
 */
export const EDITORIAL_TIME_ZONE = "Europe/London";

/** Today as YYYY-MM-DD in the editorial time zone. `en-CA` formats dates in exactly that shape. */
export function todayInEditorialZone(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: EDITORIAL_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/**
 * Whether a start date falls before today. Today itself is NOT past: an event that starts this
 * morning is still running, and warning about it would train staff to click through warnings.
 * ISO dates compare correctly as strings, so no Date parsing (and no zone drift) is involved.
 */
export function isPastStartDate(startDate: string, now: Date = new Date()): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return false;
  return startDate < todayInEditorialZone(now);
}

export const PAST_DATE_WARNING =
  "This start date is in the past. Back-dating is allowed — confirm below if that is deliberate.";

function isAbsoluteHttpUrl(value: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  return parsed.protocol === "http:" || parsed.protocol === "https:";
}

/**
 * Blocking errors only. The past-date rule is deliberately absent: it is a warning the user can
 * confirm past, not a rejection — see `isPastStartDate` and the spec requirement
 * "Past start dates raise a validation warning".
 */
export function validateEventForm(values: EventFormValues): EventFormErrors {
  const errors: EventFormErrors = {};

  if (values.title.trim() === "") {
    errors.title = "Title is required";
  }
  if (values.type === "") {
    errors.type = "Event type is required";
  }
  if (values.startDate.trim() === "") {
    errors.startDate = "Start date is required";
  }
  if (values.startTime.trim() === "") {
    errors.startTime = "Start time is required";
  }
  if (values.timezone.trim() === "") {
    errors.timezone = "Time zone is required";
  }
  if (locationShortRequiredForType(values.type) && values.locationShort.trim() === "") {
    errors.locationShort = `Short location is required for a ${values.type}`;
  }
  if (values.linkUrl.trim() === "") {
    errors.linkUrl = "Link URL is required";
  } else if (!isAbsoluteHttpUrl(values.linkUrl.trim())) {
    errors.linkUrl = "Link URL must be an absolute http or https URL";
  }
  if (!EVENT_STATUS_VALUES.includes(values.status)) {
    errors.status = "Status is required";
  }

  return errors;
}

export type HeroBannerFormValues = {
  backgroundImage: ImageRef | null;
  title: string;
  dateText: string;
  locationText: string;
  ctaText: string;
  ctaUrl: string;
  /**
   * Whether the banner goes on the public events page. Deliberately not validated: both values
   * are valid, and a hidden banner still has to pass every other rule so that it can be
   * restored exactly as it was.
   */
  showOnSite: boolean;
};

export type HeroBannerFormErrors = Partial<Record<keyof HeroBannerFormValues, string>>;

/**
 * The banner's date and location are plain display text, not a second event record — they
 * advertise one event in whatever wording reads best (design D6). The background image is the
 * one image on this feature that is required rather than optional.
 */
export function validateHeroBannerForm(values: HeroBannerFormValues): HeroBannerFormErrors {
  const errors: HeroBannerFormErrors = {};

  if (!values.backgroundImage) {
    errors.backgroundImage = "A background image is required";
  }
  if (values.title.trim() === "") {
    errors.title = "Title is required";
  }
  if (values.dateText.trim() === "") {
    errors.dateText = "Date text is required";
  }
  if (values.locationText.trim() === "") {
    errors.locationText = "Location text is required";
  }
  if (values.ctaText.trim() === "") {
    errors.ctaText = "CTA text is required";
  }
  if (values.ctaUrl.trim() === "") {
    errors.ctaUrl = "CTA URL is required";
  } else if (!isAbsoluteHttpUrl(values.ctaUrl.trim())) {
    errors.ctaUrl = "CTA URL must be an absolute http or https URL";
  }

  return errors;
}

/**
 * The zones Legal 500 actually runs events in, so staff pick a name content-api will accept
 * rather than typing one. Deliberately curated rather than the browser's full
 * `Intl.supportedValuesOf("timeZone")` list: that is around 400 entries, which is unusable in a
 * select and re-renders the whole form on every keystroke.
 */
export const COMMON_TIME_ZONES = [
  "Europe/London",
  "UTC",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "Europe/Zurich",
  "Europe/Stockholm",
  "Europe/Warsaw",
  "Europe/Istanbul",
  "Africa/Lagos",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Jakarta",
  "Asia/Singapore",
  "Asia/Kuala_Lumpur",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Sydney",
  "Pacific/Auckland",
  "America/Sao_Paulo",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
] as const;

/**
 * The curated list, with the event's own zone added if it is not on it — otherwise editing an
 * event saved with an unusual zone would silently rewrite it to whichever option happened to
 * be first.
 */
export function timeZoneOptions(current?: string): Array<{ value: string; label: string }> {
  const zones: string[] = [...COMMON_TIME_ZONES];
  if (current && !zones.includes(current)) {
    zones.unshift(current);
  }
  return zones.map(zone => ({ value: zone, label: zone }));
}
